<template>
    <div>
        <slot />
    </div>
</template>

<script setup>
    import { useEnvMapLoader } from '#imports';
    import { Color, Euler, Group, LinearSRGBColorSpace, Mesh, PlaneGeometry, Vector3 } from 'three';
    import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';

    import { LoaderEvent } from '@resn/gozer-loading';
    import { disposeObject } from '@resn/gozer-three/helpers';
    import { usePane, useRafBool, useRenderer, useResize, useThreeObject } from '@resn/gozer-vue';
    import { useLoader } from '@resn/gozer-vue/loading';
    import { gsap } from '@resn/gsap';

    import { getTextures } from './data';
    import { NebulaEffect } from './GoldenNebula';
    import ShineLayer from './GoldenShine';
    import TicketMaterial from '~/components/gl/materials/GoldenTicketMaterial';
    import { loadBitmapTexture } from '~/libs/three/loadBitmapTexture';
    import { useAudio } from '~/providers/AudioProvider';
    import { useGlobalAssets } from '~/providers/GlobalAssets';

    const { renderer } = useRenderer();

    const props = defineProps({
        active: { type: Boolean, default: true },
        revealed: { type: Boolean, default: false },

        data: {
            default: {
                id: '',
                imageFront: '/textures/ticket/metallic-front@lg.webp',
                imageBack: '/textures/ticket/metallic-back@lg.webp',
                ticketType: 'default',
            },
        },

        hasCompressedTextures: { type: Boolean, default: true },
        hasAutoLoad: { type: Boolean, default: true },
        hasPane: { type: Boolean, default: true },
        hasAmbientMotion: { type: Boolean, default: true },
    });

    const visible = ref(false);
    const activeComputed = computed(() => props.active && propsReactive.active);
    const revealedComputed = computed(() => props.revealed && propsReactive.active);

    const audio = useAudio();

    const propsTween = {
        delay: 0,
    };

    const propsReactive = reactive({
        active: false,

        speed: 2,
        motionAmb: 1,

        wipeRevealPr: 0,
        wipeAngle: -110,

        envMapRotation: new Euler(0, 0.2, -0.5),
    });

    const { loadEnvMap } = useEnvMapLoader();

    // Vectors for ambient motion
    const vPosStart = new Vector3();
    const vPosAmbient = new Vector3();
    const vPosOffset = new Vector3();

    const vRotStart = new Vector3();
    const vRotAmbient = new Vector3();
    const vRotOffset = new Vector3();
    const { object, props: propsObject } = useThreeObject(null, {
        name: 'GoldenTicketWrapper',
        props: { s: 0.85 },
    });

    const paramsNebula = reactive({});
    const paramsShine = reactive({});

    const propsMaterial = reactive({
        roughness: 0.14,
        metalness: 0.52,
    });

    const commonShaderProps = {
        roughness: propsMaterial.roughness,
        metalness: propsMaterial.metalness,
        envMapRotation: propsReactive.envMapRotation,
        opacity: 1.0,
    };
    const commonShaderOpts = {
        flipUV: props.hasCompressedTextures,
    };

    const shaderFront = new TicketMaterial(commonShaderProps, commonShaderOpts);
    shaderFront.uniforms.uPreRevealColor1.value = new Color('#f4c047');
    shaderFront.uniforms.uPreRevealColor2.value = new Color('#cdb15a');

    const shaderBack = new TicketMaterial(commonShaderProps, commonShaderOpts);
    shaderBack.defines.USE_WIPE = false;
    shaderBack.defines.USE_PRE_REVEAL = false;

    const grpTicket = new Group();
    grpTicket.scale.setScalar(0.68);
    object.add(grpTicket);

    let goldenNebula, goldenShine;

    // Create a simple texture cache at the component level
    const textureCache = new Map();

    // Initialize the ticket objects
    const setupObjects = () => {
        const geometry = new PlaneGeometry(1, 1.76);

        // Load and generate environment map from EXR texture
        loadEnvMap(renderer, '/textures/env-map/env-map-global.exr').then(({ map }) => {
            shaderFront.envMap = map.value;
            shaderBack.envMap = map.value;
        });

        // Create front and back meshes
        const meshFront = new Mesh(geometry, shaderFront);
        meshFront.position.z = 0.01;

        const meshBack = new Mesh(geometry, shaderBack);
        meshBack.position.z = -0.01;
        meshBack.rotation.y = Math.PI;

        grpTicket.add(meshFront);
        grpTicket.add(meshBack);

        // ― Nebula
        goldenNebula = new NebulaEffect(object, renderer);
        goldenNebula.init().then(() => {
            goldenNebula.updatePixelRatio();
        });
        for (const key in goldenNebula.params) paramsNebula[key] = goldenNebula.params[key];

        // ― Shine
        goldenShine = new ShineLayer(object);
        for (const key in goldenShine.uniforms) paramsShine[key] = goldenShine.uniforms[key].value;

        reset();
    };

    // Store initial position and rotation
    onMounted(() => {
        setImages({ back: props.data.imageBack, front: props.data.imageFront });
        reset();
    });

    onBeforeUnmount(() => {
        dispose();
    });

    // Update materials with loaded textures
    const updateTextures = (textures) => {
        if (shaderFront) {
            shaderFront.map = textures.mapDiffuseGold;
            shaderFront.innerMap = textures.mapDiffuseInner;
            shaderFront.normalMap = textures.mapNormal;

            shaderFront.uniforms.uTexPreRevealSticker.value = textures.mapSticker;
        }

        if (shaderBack) {
            shaderBack.map = textures.mapDiffuseGold;
            shaderBack.innerMap = textures.mapDiffuseInner;
            shaderBack.normalMap = textures.mapNormal;
        }
    };

    const updateUniforms = () => {
        shaderFront.uniforms.uWipeRevealPr.value = propsReactive.wipeRevealPr;
        shaderFront.uniforms.uWipeAngle.value = propsReactive.wipeAngle;
    };

    const show = ({ delay = 0 } = {}) => {
        propsTween.delay = delay;
        visible.value = true;
    };
    const hide = ({ delay = 0 } = {}) => {
        propsTween.delay = delay;
        visible.value = false;
    };

    let tlRevealed;
    const setRevealed = (bool) => {
        tlRevealed?.kill();
        if (bool) {
            // Revealing the ticket (flipping to back)
            tlRevealed = gsap
                .timeline()
                .addLabel('lblExplosion', 0.6)
                // First phase: Start turn and prepare effects
                .add(() => audio.play('goldenTicketReveal'), 0)
                .to(goldenShine.position, { z: 0.2, duration: 0.6, ease: 'power2.out' }, 0)

                .to(vPosOffset, { z: -0.5, duration: 0.6, ease: 'sine.in' }, 0)
                .to(vPosOffset, { z: 0.5, duration: 0.6, ease: 'back.out(2)' }, 'lblExplosion')

                .to(vRotOffset, { y: Math.PI * 0.1, duration: 0.5, ease: 'sine.in' }, 0)
                .to(vRotOffset, { y: -Math.PI, duration: 1, ease: 'back.out(2)' }, '>')

                .add(() => {
                    // Trigger explosion when the card is halfway through the flip
                    if (goldenNebula) {
                        goldenNebula?.reset();
                        goldenNebula?.show();
                        goldenNebula?.triggerExplosion(paramsNebula.explosionStrength);
                    }
                    // Trigger shine reveal effect
                    goldenShine?.reveal({ intensity: 3, duration: 0.8, delay: 0.2 });
                }, 'lblExplosion-=0.2')

                .to(vPosOffset, { z: 0, duration: 1, ease: 'power1.inOut' }, 'lblExplosion+=0.8');
        } else {
            // Turning back to front (unreveal)
            tlRevealed = gsap
                .timeline()
                .add(() => goldenNebula?.hide({ duration: 0.5 }), 0)
                .to(vRotOffset, { y: 0, duration: 0.8, ease: 'back.out(1.2)' }, 0)
                .to(goldenShine.position, { z: -0.2, duration: 0.4, ease: 'power1.out' }, 0);
        }
    };

    const animateIn = ({ delay = 0 } = {}) => {
        const motionAmb = Math.max(+props.hasAmbientMotion, 0.001);

        tlShow?.kill();
        tlShow = gsap
            .timeline({
                delay,
                onStart: () => {
                    propsObject.v = true;
                    propsReactive.active = true;
                },
            })
            .to(propsReactive, { motionAmb, duration: 2, ease: 'sine.out' }, 0.5)
            .add(goldenShine?.show({ duration: 1.5, intensity: 0.8, ease: 'power2.out' }), 0.8)
            .add(() => audio.play('goldenTicketShing'), 2)
            .to(propsReactive, { wipeRevealPr: 1, duration: 1, ease: 'power2.inOut' }, 2)
            .to(vPosStart, { z: 0.6, duration: 1, ease: 'power2.inOut' }, 2)
            .add(goldenShine?.flash({ intensity: 2.5, duration: 1 }), 2.4);
    };

    const animateOut = ({ delay = 0 } = {}) => {
        tlShow?.kill();
        tlShow = gsap
            .timeline({
                delay,
                onComplete: () => reset(),
            })
            // Start by hiding the shine
            .add(() => {
                goldenShine?.hide({ duration: 0.8, ease: 'power2.out' });
                goldenNebula?.hide({ duration: 0.8, ease: 'power2.out' });
            }, 0)
            .to(vPosStart, { y: -2, z: 1.2, duration: 1.2, ease: 'power1.inOut' }, 0)
            .to(vRotStart, { x: 1.5, duration: 1.2, ease: 'power1.inOut' }, 0)
            .to(propsReactive, { motionAmb: 0, duration: 0.8, ease: 'sine.out' }, 0);
    };

    // Set visibility with animation
    let tlShow;
    const setVisible = (bool) => {
        if (bool) animateIn(propsTween);
        else animateOut(propsTween);
    };

    // Improve the setImages function
    const setImages = ({ back: backUrl, front: frontUrl } = {}) => {
        if (frontUrl && shaderFront) {
            if (!textureCache.has(frontUrl)) {
                loadBitmapTexture(frontUrl).then((t) => {
                    textureCache.set(frontUrl, t);

                    shaderFront.rgbMap?.dispose();
                    shaderFront.rgbMap = t;
                });
            } else shaderFront.rgbMap = textureCache.get(frontUrl);
        }

        if (backUrl && shaderBack) {
            if (!textureCache.has(backUrl)) {
                loadBitmapTexture(backUrl).then((t) => {
                    textureCache.set(backUrl, t);

                    shaderBack.rgbMap?.dispose();
                    shaderBack.rgbMap = t;
                });
            } else shaderBack.rgbMap = textureCache.get(backUrl);
        }
    };

    watch(
        () => props.data.imageBack,
        () => setImages({ back: props.data.imageBack })
    );
    watch(
        () => props.data.imageFront,
        () => setImages({ front: props.data.imageFront })
    );

    const update = ({ timestamp, delta }) => {
        const ts = timestamp / 1000;
        const { speed, motionAmb } = propsReactive;

        // Calculate ambient position (gentle floating)
        vPosAmbient.set(0, Math.sin(ts * speed * 0.2), 0).multiplyScalar(0.04 * motionAmb);
        // .multiplyScalar(0);

        // Calculate ambient rotation (gentle swaying)
        vRotAmbient
            .set(
                Math.sin(ts * speed * 0.4) * 0.2,
                Math.sin(ts * speed * 0.6) * 0.4,
                Math.cos(ts * speed * 0.3) * -0.02
            )
            .multiplyScalar(0.5 * motionAmb);

        shaderFront.uniforms.uTime.value = ts;

        // Apply ambient motion to the object
        const vPos = vPosStart.clone().add(vPosAmbient).add(vPosOffset);
        const vRot = vRotStart.clone().add(vRotAmbient).add(vRotOffset);

        grpTicket.position.copy(vPos);
        grpTicket.rotation.setFromVector3(vRot);

        // Animate goldenNebula effect if active and visible
        goldenNebula?.animate({ deltaTime: delta / 1000 });

        // Animate goldenShine effect if active
        goldenShine?.animate({ time: timestamp / 1000 });
    };
    useRafBool(activeComputed, update);

    useResize(({ width, height }) => {
        if (goldenShine) goldenShine.updateAspect(width, height);
    });

    // Clean up resources
    const dispose = () => {
        disposeObject(object);
        // disposeEnvMaps();

        textureCache.forEach((texture) => texture.dispose());
        textureCache.clear();

        goldenNebula?.dispose();
        goldenNebula = null;
        goldenShine?.dispose();
        goldenShine = null;
    };

    // Reset the ticket state
    const reset = () => {
        propsObject.v = false;
        propsReactive.active = false;

        // Reset ambient motion
        propsReactive.motionAmb = 0;
        propsReactive.wipeRevealPr = 0;

        vRotStart.set(0, 0, 0);
        vRotOffset.set(0, 0, 0);
        vRotAmbient.set(0, 0, 0);

        vPosAmbient.set(0, 0, 0);
        vPosStart.set(0, 0, -0.1);
        vPosOffset.set(0, 0, 0);

        // Reset position and rotation
        grpTicket.position.copy(vPosStart);
        grpTicket.rotation.setFromVector3(vRotStart);

        // Reset shine concisely
        goldenShine?.reset();
        // Reset nebula
        goldenNebula?.reset();
    };

    watch(visible, setVisible);
    watch(revealedComputed, setRevealed);

    watch(propsReactive, updateUniforms);

    // Set up loader for textures
    const { hasAutoLoad, hasCompressedTextures } = props;
    let loader;

    if (hasAutoLoad) {
        loader = useLoader(getTextures({ compressed: hasCompressedTextures })).once(
            LoaderEvent.LOAD_COMPLETE,
            ({ data }) => {
                // Process loaded textures
                for (const key in data) {
                    const map = data[key];
                    map.colorSpace = LinearSRGBColorSpace;
                    map.flipY = true;
                    map.needsUpdate = true;
                }

                updateTextures(data);
            }
        );
    }

    useGlobalAssets(({ data }) => {
        shaderFront.uniforms.uNoise.value = data.pNoise;
    });

    onMounted(() => {
        setupObjects();
        loader?.start();
    });

    // Debugging controls with Pane if needed
    if (props.hasPane) {
        const pane = usePane(
            [
                {
                    value: propsMaterial,
                    options: {
                        onChange: () => {
                            if (shaderFront) {
                                shaderFront.roughness = propsMaterial.roughness;
                                shaderFront.metalness = propsMaterial.metalness;
                            }
                            if (shaderBack) {
                                shaderBack.roughness = propsMaterial.roughness;
                                shaderBack.metalness = propsMaterial.metalness;
                            }
                        },
                    },
                },
                { value: propsReactive },
            ],
            {
                title: 'Golden Ticket',
                expanded: true,
            }
        );

        onMounted(() => {
            if (pane) {
                // Nebula folder
                const fNebula = pane?.addFolder({ title: 'Nebula', expanded: false });
                for (const key in paramsNebula) {
                    fNebula.addBinding(paramsNebula, key).on('change', () => {
                        if (goldenNebula) {
                            Object.assign(goldenNebula.params, paramsNebula);
                            goldenNebula.updateUniforms();
                        }
                    });
                }

                // Shine folder
                const fShine = pane?.addFolder({ title: 'Shine', expanded: false });
                for (const key in paramsShine) {
                    fShine
                        .addBinding(
                            paramsShine,
                            key,
                            key == 'lightColor' ? { color: { type: 'float' } } : {}
                        )
                        .on('change', () => {
                            const uniforms = goldenShine.uniforms;
                            uniforms.uLightPosition.value.copy(paramsShine.uLightPosition);
                            uniforms.uLightIntensity.value = paramsShine.uLightIntensity;
                            uniforms.uRaySpeed.value = paramsShine.uRaySpeed;
                            uniforms.uLargeVariation.value = paramsShine.uLargeVariation;
                            uniforms.lightColor.value.set(paramsShine.lightColor);
                            uniforms.uGammaBorder.value = paramsShine.uGammaBorder;
                            uniforms.uUVScale.value.set(
                                paramsShine.uUVScale.x,
                                paramsShine.uUVScale.y
                            );
                        });
                }
            }
        });
    }

    defineExpose({
        show,
        hide,
        reset,
        update,
        dispose,
    });
</script>
